import { useParams } from "react-router-dom";
import BackLink from "./BackLink";
import {
  CircularProgress,
  Grid,
  Chip,
  Paper,
  Typography
} from "@mui/material";
import { cardSX } from '../utilities/CSS';
import UserSummary from "./UserSummary";
import ServiceHistory from "./ServiceHistory";
import { useContext, useEffect, useState } from "react";
import adminFormsService from "../services/AdminFormsService";
import FormSubmission from "./FormSubmission";
import { iUserDTO, iUserLink, iUserServiceHistoryDTO } from "../utilities/APIInterfaces";
import userService from "../services/UserService";
import { OrganizationContextProvider } from "../contexts/OrganizationContext";
import { OrganizationContext } from '../contexts/OrganizationContext';
import { iAdminFormSubmissionsData } from "../contexts/AdminFormSubmissionsDataContext";
import formsService from "../services/FormsService";
import EmailIcon from '@mui/icons-material/Email';
import NotificationForm from './notifications/NotificationForm';
import { AdminFormDataContextProvider } from '../contexts/AdminFormDataContext';
import KeycloakService from "../services/KeycloakService";
import { VerifierType } from "../utilities/FormEnums";
import DownloadIcon from '@mui/icons-material/Download';
import reportService from '../services/ReportService';
import { enqueueSnackbar } from 'notistack';

export interface iUserSummary {
  name: string;
  email: string;
  phone: string;
  address: string;
  ssn: string | null;
  dob: string | null;
  studentId: string | null;
}

export default function AdminApplicantForm() {
  const { userId, submissionUuid } = useParams();
  const [user, setUser] = useState<iUserDTO>();
  const [form, setForm] = useState({} as any);
  const [contributors, setContributors] = useState<iUserLink[]>([]);
  const [submission, setSubmission] = useState<iAdminFormSubmissionsData | undefined>(undefined);
  const [serviceHistory, setServiceHistory] = useState<iUserServiceHistoryDTO[]>([]);
  const [isServiceHistoryLoaded, setIsServiceHistoryLoaded] = useState(false);
  const [isNotificationFormOpen, setIsNotificationFormOpen] = useState(false);
  const { organization } = useContext(OrganizationContext);
  const [showNotificationOption, setShowNotificationOption] = useState(false);
  const [isDownloadingBinder, setIsDownloadingBinder] = useState(false);

  const reloadServiceHistory = () => {
    setIsServiceHistoryLoaded(false);
  };

  const handleDownloadBinder = async () => {

    if (!organization || !form || !submission || !user) return;

    try {
      setIsDownloadingBinder(true);

      const pdfBlob = await reportService.GetBinderPDF({
        opeId: organization.opeid,
        formUuid: form.uuid,
        requestedBy: user.authId,
        submissionUuids: [submission.uuid],
        downloadNow: true
      }, (v) => v);

      if (!pdfBlob) {
        enqueueSnackbar(<div>
          <div>Sorry, we couldn't download the binder PDF.</div>
          <div style={{ margin: '.5em 0' }}>Please try again later.</div>
          If the problem continues, contact support.
        </div>, { variant: 'error' });
        return;
      }

      // Create a temporary link and trigger download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'binder.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Error downloading binder PDF:', error);
      enqueueSnackbar(<div>
        <div>Sorry, we couldn't download the binder PDF.</div>
        <div style={{ margin: '.5em 0' }}>Error:</div>
        {(error as Error).message}
      </div>, { variant: 'error' });
    } finally {
      setIsDownloadingBinder(false);
    }
  };

  const pageSX = {
    h1: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 1.5,
      fontWeight: '600',
      fontSize: '1.5rem',
      mb: 1.75
    },
    chip: {
      backgroundColor: '#fff',
      borderColor: '#e1e2e5',
      px: 2,
      '& .MuiSvgIcon-root': {
        ml: 0,
        mr: 0.5
      },
      '& .MuiCircularProgress-root': {
        ml: 0,
        mr: 1
      },
      '& .MuiChip-label': {
        fontSize: '0.875rem',
        fontWeight: 'normal',
        p: 0
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (userId === undefined) return;
      const result = await userService.GetUser(userId, (x) => x);
      setUser(result);

      const allContributors = await userService.GetUserLinks(userId);
      setContributors(allContributors.filter(contributor => contributor.status === 'Approved'));

      const submission = await adminFormsService.GetFormSubmisisonByUuid(submissionUuid ?? '', (v: boolean) => { });
      setSubmission(submission);

      const form = await formsService.GetFormByUuid(submission?.formUuid ?? '', (x) => x);
      if (form.verifierTypeId == VerifierType.INCEPTIA) {
        if (form.schoolReviewEmail) {
          setShowNotificationOption(true);
        }
        else if (KeycloakService.hasGroup(['Admin', 'Manager', 'Operations', 'SrOperations'])) {
          setShowNotificationOption(true);
        }
      } else if (KeycloakService.hasGroup(['Admin', 'InstitutionAdmin', 'Institution'])) {
        setShowNotificationOption(true);
      }
      setForm(form);
    })();
  }, []);

  useEffect(() => {
    if (!isServiceHistoryLoaded) {
      userService.GetUserServiceHistory(userId, submissionUuid ?? '', (x) => x)
        .then(result => setServiceHistory(result))
        .finally(() => setIsServiceHistoryLoaded(true));
    }
  }, [isServiceHistoryLoaded]);

  return (
    <OrganizationContextProvider>
      <AdminFormDataContextProvider>
        <BackLink title='Back to Form Submissions' />
        <Typography variant='h1' sx={pageSX.h1}>
          {`${user?.firstName} ${user?.lastName}`}
          <Chip
            label='Student'
            variant='outlined'
            sx={pageSX.chip}
          />
          <Chip
            label={isDownloadingBinder ? 'Downloading Binder...' : 'Download Binder'}
            variant='outlined'
            sx={pageSX.chip}
            onClick={handleDownloadBinder}
            icon={
              isDownloadingBinder
                ? <CircularProgress color="inherit" size={20} />
                : <DownloadIcon />
            }
            disabled={!organization || !form || !submission || !user}
          />
          {showNotificationOption && <Chip
            label='Send Notification'
            variant='outlined'
            sx={pageSX.chip}
            onClick={() => {
              setIsNotificationFormOpen(true);
            }}
            icon={<EmailIcon />}
          />}
        </Typography>
        {isNotificationFormOpen && user &&
          <NotificationForm
            isOpenDialog={isNotificationFormOpen}
            setIsOpenDialog={setIsNotificationFormOpen}
            users={[{
              auth_id: user.authId ?? '',
              name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
              email_address: user.email,
              organization: organization ?? null,
              phone_number: user.phone ?? null,
              submission_form_id: submission?.id ?? undefined
            }]}
            allowCustomMessageTemplates={true}
            onClose={reloadServiceHistory}
          />
        }

        <Grid container spacing={3.75} sx={{ mb: 5 }}>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ ...cardSX.paperMinHeight, px: 3.25, py: 3.75 }}>
              <UserSummary user={user} displayContributors={submission?.hasContributor} contributors={contributors} />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper sx={{ ...cardSX.paperMinHeight, px: 3.25, py: 3.75 }}>
              <ServiceHistory
                userId={userId || ''}
                setIsServiceHistoryLoaded={setIsServiceHistoryLoaded}
                submissionUuid={submissionUuid || ''}
                serviceHistory={serviceHistory}
              />
            </Paper>
          </Grid>
        </Grid>

        <Typography variant='h2' sx={{ fontWeight: '600', fontSize: '1.5rem', mb: 2 }}>
          Form Submission - {form.name}
        </Typography>

        <Paper sx={{ ...cardSX.paper, p: '1px' }}>
          {submission !== undefined && Object.keys(form).length > 0 && <FormSubmission
            form={form}
            submission={submission}
            setSubmission={setSubmission}
            setIsServiceHistoryLoaded={setIsServiceHistoryLoaded}
            verifierTypeId={form.verifierTypeId}
          />}
        </Paper>
      </AdminFormDataContextProvider>
    </OrganizationContextProvider>

  )
}